import React, { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import { Button } from '@givebutter/primitives/button';
import { useForwardedRef } from '@givebutter/react-util';
import { twMerge } from 'tailwind-merge';

const FilterListItemButton = forwardRef(
    (
        props: Omit<ComponentPropsWithoutRef<'button'>, 'color'>,
        ref: ForwardedRef<HTMLButtonElement>,
    ) => {
        const { children, className = '', onMouseEnter, onKeyDown, ...rest } = props;
        const forwardedRef = useForwardedRef(ref);

        const arrowKeyUpOrDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
            const listBox = forwardedRef.current?.closest('[role="listbox"]') as
                | HTMLElement
                | null
                | undefined;
            const currentOption = e.currentTarget;

            if (listBox) {
                const allOptions = listBox.querySelectorAll('[role="option"]');

                let currentTargetIndex = -1;
                allOptions.forEach((element, index) => {
                    if (element === currentOption) {
                        currentTargetIndex = index;
                    }
                });

                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const possibleNextOption = allOptions[
                        currentTargetIndex + 1
                    ] as HTMLElement | null;

                    if (possibleNextOption) {
                        possibleNextOption.focus();
                    }
                }

                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const possibleNextOption = allOptions[
                        currentTargetIndex - 1
                    ] as HTMLElement | null;

                    if (possibleNextOption) {
                        possibleNextOption.focus();
                    }
                }
            }
        };

        return (
            <Button
                ref={ref}
                variant="none"
                color="neutral"
                size="sm"
                className={twMerge(
                    'w-full justify-start rounded-md p-2 font-medium leading-none before:!hidden hover:bg-gray-100 focus:bg-gray-100',
                    className,
                )}
                role={'option'}
                aria-label={'Filter option'}
                aria-selected={'false'}
                onKeyDown={e => {
                    arrowKeyUpOrDown(e);
                    onKeyDown?.(e);
                }}
                onMouseEnter={e => {
                    if (
                        document.activeElement?.tagName.toLowerCase() !== 'input' &&
                        document.activeElement !== e.currentTarget
                    ) {
                        forwardedRef.current?.focus();
                    }
                    onMouseEnter?.(e);
                }}
                {...rest}
            >
                {children}
            </Button>
        );
    },
);

export { FilterListItemButton };
